"use client"

import React, {useEffect, useMemo} from "react";
import {useForm} from "react-hook-form";
import {valibotResolver} from "@hookform/resolvers/valibot";
import * as v from "valibot";
import {ServiceContactSchema} from "@root/server/routes/web/order_wizard/[order_edit_id]/moving/moving.shared.ts";
import {RhfInput} from "@root/server/client/esweb/components/ui/rhf/RhfInput.tsx";
import {RhfTextarea} from "@root/server/client/esweb/components/ui/rhf/RhfTextarea.tsx";
import {DatePickerField} from "@root/server/client/esweb/src/pages/web/shared/date-picker-field.tsx";
import {ReactWrapperController} from "@root/server/client/esweb/src/lib/stimulus/ReactWrapperController.ts";
import {Context} from "@hotwired/stimulus";
import {ContinueSubmitButton} from "@root/server/client/esweb/src/pages/web/shared/continue-submit-button.tsx";
import {FormServerErrors} from "@root/server/client/esweb/src/pages/web/shared/form-server-errors.tsx";
import {useFormSubmission} from "@root/server/client/esweb/src/lib/hooks/useFormSubmission.ts";


export const name = 'pwm--service_contact_form';

export default class extends ReactWrapperController {
    constructor(context: Context) {
        super(context, (props) => <Service_contact_formStimulus {...props}/>)
    }
}


type MovingContactFormValues = v.InferInput<typeof ServiceContactSchema>;

interface MovingContactFormProps {
    initialData?: Partial<MovingContactFormValues>;
    submitUrl: string;
}

const parseMovingDates = (dates?: MovingContactFormValues["serviceDates"]) => {
    if (!Array.isArray(dates)) {
        return [] as Date[];
    }

    return dates
        .map((date) => (date instanceof Date ? date : new Date(date)))
        .filter((date) => !Number.isNaN(date.getTime()));
};

export function Service_contact_formStimulus({initialData, submitUrl}: MovingContactFormProps) {
    const defaultValues = useMemo<MovingContactFormValues>(() => ({
        email: initialData?.email ?? "",
        phone: initialData?.phone ?? "",
        serviceDates: parseMovingDates(initialData?.serviceDates),
        note: initialData?.note ?? "",
    }), [initialData]);

    const {control, handleSubmit, formState, reset} = useForm<MovingContactFormValues>({
        resolver: valibotResolver(ServiceContactSchema),
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues,
    });

    useEffect(() => {
        reset(defaultValues);
    }, [defaultValues, reset]);

    const {execute, isSubmitting, serverErrors} = useFormSubmission<MovingContactFormValues>({submitUrl});

    const {errors} = formState;

    return (
        <form onSubmit={handleSubmit(execute)} className="space-y-6">
            <section className="p-4 max-sm:-mx-4">
                <div className="grid gap-4 md:grid-cols-2">
                    <RhfInput
                        control={control}
                        name="email"
                        label="E-mail *"
                        inputProps={{
                            type: "email",
                            placeholder: "např. jan.novak@email.cz",
                            autoComplete: "email",
                        }}
                    />

                    <RhfInput
                        control={control}
                        name="phone"
                        label="Telefon *"
                        inputProps={{
                            type: "phone",
                            placeholder: "např. +420 ...",
                            autoComplete: "phone",
                        }}
                    />
                </div>
            </section>

            <section className="p-4 max-sm:-mx-4">
                <DatePickerField
                    control={control}
                    name="serviceDates"
                    label="Preferované termíny"
                    placeholder="Vyberte termíny"
                    multiple={true}
                    error={errors.serviceDates?.message}
                />
            </section>


            <section className="p-4 max-sm:-mx-4">
                <RhfTextarea
                    control={control}
                    name="note"
                    label="Doplňující informace"
                    textareaProps={{
                        placeholder: "Upřesněte specifické požadavky nebo důležité detaily (max. 500 znaků)",
                        maxLength: "500",
                    }}
                />
            </section>

            <FormServerErrors errors={serverErrors} />

            <ContinueSubmitButton {...{isSubmitting}}/>
        </form>
    );
}
